/**
 * VirtualTour — V3 Day 1 (2026-05-08)
 *
 * Modal-mode 360° viewer for KEY2 unit panoramas. Uses Pannellum
 * (lazy-loaded from CDN on first open) with a multi-scene config so
 * room switches are instant after the first image loads. The room
 * catalog is read from src/data/assets-manifest.json — no hardcoded
 * URLs in this file.
 *
 * Touch-friendly: 60×60 controls, swipe-to-pan via Pannellum's
 * native touch handlers, ESC + overlay-click to close.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, X, Maximize, RotateCw, AlertTriangle } from 'lucide-react';
import manifest from '@data/assets-manifest.json';
import {
  loadPannellum,
  type PannellumViewer,
  type PannellumConfig,
} from '@lib/pannellumLoader';
import { useT, type StringKey } from '@lib/i18n';

type UnitTypeKey = '1bhk' | '2bhk' | '3bhk';

interface PanoramaSet {
  [room: string]: string;
}

interface VirtualTourProps {
  /** '1BHK' | '2BHK' | '3BHK' — the upper-case form from units.json. */
  unitType: string;
  /** Optional starting room (must exist for the unit type). */
  initialRoom?: string;
  onClose: () => void;
}

// Bumping this forces the browser to refetch panoramas instead of
// reusing a disk-cached entry. Reason: panoramas were served before
// the assets-cors Worker existed, and those cached entries have no
// Access-Control-Allow-Origin header. Pannellum's XHR is then
// blocked by the same-URL cache hit, even though a fresh fetch from
// CF would now succeed. Bump this string when CORS-affecting
// changes are deployed for /key2/panoramas/*.
const PANORAMA_CACHE_VERSION = 'cors20260508';

function withVersion(url: string): string {
  return url.includes('?')
    ? `${url}&v=${PANORAMA_CACHE_VERSION}`
    : `${url}?v=${PANORAMA_CACHE_VERSION}`;
}

const PREFERRED_ROOM_ORDER = [
  'living',
  'kitchen',
  'master-bedroom',
  'bedroom1',
  'bedroom2',
  'master-bath',
  'living-bath',
  'passage-bath',
  'laundry',
];

function normalizeUnitType(t: string): UnitTypeKey | null {
  const k = t.toLowerCase();
  if (k === '1bhk' || k === '2bhk' || k === '3bhk') return k;
  return null;
}

function orderRooms(rooms: string[]): string[] {
  return [...rooms].sort((a, b) => {
    const ai = PREFERRED_ROOM_ORDER.indexOf(a);
    const bi = PREFERRED_ROOM_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export default function VirtualTour({
  unitType,
  initialRoom,
  onClose,
}: VirtualTourProps) {
  const t = useT();
  const typeKey = normalizeUnitType(unitType);
  const panoramas: PanoramaSet | null = typeKey
    ? (manifest.panoramas as Record<string, PanoramaSet>)[typeKey] ?? null
    : null;

  const rooms = panoramas ? orderRooms(Object.keys(panoramas)) : [];
  const startRoom =
    initialRoom && rooms.includes(initialRoom)
      ? initialRoom
      : rooms.includes('living')
        ? 'living'
        : rooms[0];

  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<PannellumViewer | null>(null);
  const [currentRoom, setCurrentRoom] = useState<string | undefined>(startRoom);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);

  // ─── Lock body scroll + Esc to close ─────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // ─── Lazy load Pannellum + create viewer once ────────────────
  useEffect(() => {
    if (!panoramas || !startRoom) {
      setPhase('error');
      setErrorMsg(t('vr.unavailable'));
      return;
    }
    let cancelled = false;
    loadPannellum()
      .then((P) => {
        if (cancelled || !containerRef.current) return;
        const config: PannellumConfig = {
          default: {
            firstScene: startRoom,
            autoLoad: true,
            sceneFadeDuration: 350,
            showControls: false, // we draw our own
          },
          // V3 Day 1 fix (2026-05-08) — required so Pannellum sets
          // Image.crossOrigin="anonymous" on every panorama load.
          // R2 is cross-origin to the app and WebGL textures need
          // an anonymous CORS request or the upload fails with
          // "could not be accessed". One option propagates to every
          // scene + every loadScene() call below.
          crossOrigin: 'anonymous',
          scenes: Object.fromEntries(
            Object.entries(panoramas).map(([room, url]) => [
              room,
              {
                type: 'equirectangular',
                panorama: withVersion(url),
                hfov: 100,
                autoLoad: room === startRoom,
              },
            ])
          ),
        };
        viewerRef.current = P.viewer(containerRef.current, config);
        setPhase('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErrorMsg(e instanceof Error ? e.message : String(e));
        setPhase('error');
      });
    return () => {
      cancelled = true;
      try {
        viewerRef.current?.destroy();
      } catch {
        /* ignore */
      }
      viewerRef.current = null;
    };
    // panoramas object is stable per modal mount (manifest is JSON);
    // re-running the effect on every render would tear down the viewer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType]);

  // ─── Switch scene without rebuilding the viewer ──────────────
  const switchTo = (room: string) => {
    if (room === currentRoom) return;
    setCurrentRoom(room);
    try {
      viewerRef.current?.loadScene(room);
    } catch {
      /* viewer may not exist if scene id mismatch — silently fallback */
    }
  };

  // ─── Preload on chip hover (cheap UX win) ────────────────────
  const handleHover = (room: string) => {
    if (!panoramas) return;
    const src = panoramas[room];
    if (!src) return;
    const img = new Image();
    img.src = withVersion(src);
  };

  const toggleAutoRotate = () => {
    if (!viewerRef.current) return;
    if (autoRotate) viewerRef.current.stopAutoRotate();
    else viewerRef.current.startAutoRotate(-2); // gentle leftward spin
    setAutoRotate(!autoRotate);
  };

  const toggleFullscreen = () => {
    try {
      viewerRef.current?.toggleFullscreen();
    } catch {
      /* some browsers block this without user gesture — viewer may already be in user-driven gesture */
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('vr.title')}
      className="fixed inset-0 z-50 bg-black/95 flex flex-col animate-fade-in"
      onClick={(e) => {
        // overlay click closes only when clicking the backdrop itself
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Top control bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/60 text-white no-print">
        <span className="text-sm font-semibold opacity-90">{t('vr.title')}</span>
        <div className="flex items-center gap-2">
          <IconButton
            label={t('vr.autoRotate')}
            active={autoRotate}
            onClick={toggleAutoRotate}
            disabled={phase !== 'ready'}
          >
            <RotateCw className="w-5 h-5" />
          </IconButton>
          <IconButton
            label={t('vr.fullscreen')}
            onClick={toggleFullscreen}
            disabled={phase !== 'ready'}
          >
            <Maximize className="w-5 h-5" />
          </IconButton>
          <IconButton label={t('vr.close')} onClick={onClose} accent="close">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </div>

      {/* Viewer area */}
      <div className="relative flex-1">
        <div
          ref={containerRef}
          className="absolute inset-0 bg-black"
          aria-hidden={phase !== 'ready'}
        />
        {phase === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 pointer-events-none">
            <Loader2 className="w-10 h-10 animate-spin text-gold" />
            <p className="text-sm">{t('vr.loading')}</p>
          </div>
        )}
        {phase === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-300 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <p className="text-sm max-w-md leading-relaxed">{t('vr.error')}</p>
            {errorMsg && (
              <p className="text-[11px] text-white/40 num-en break-all max-w-md">
                {errorMsg}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Room chip rail */}
      {rooms.length > 0 && (
        <div className="px-3 py-3 bg-black/70 backdrop-blur no-print">
          <div className="flex flex-wrap gap-2 justify-center max-w-5xl mx-auto">
            {rooms.map((room) => (
              <RoomChip
                key={room}
                label={t(`vr.rooms.${room}` as StringKey)}
                active={room === currentRoom}
                onClick={() => switchTo(room)}
                onMouseEnter={() => handleHover(room)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function IconButton({
  label,
  children,
  onClick,
  active,
  disabled,
  accent,
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  accent?: 'close';
}) {
  const base =
    'inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  const colorCls =
    accent === 'close'
      ? 'bg-white/10 hover:bg-red-500/70 text-white'
      : active
        ? 'bg-gold text-navy'
        : 'bg-white/10 hover:bg-white/20 text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      className={`${base} ${colorCls}`}
    >
      {children}
    </button>
  );
}

function RoomChip({
  label,
  active,
  onClick,
  onMouseEnter,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onMouseEnter}
      aria-pressed={active}
      className={[
        'px-4 py-2 rounded-full text-sm font-medium transition-colors min-h-[40px]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-gold',
        active
          ? 'bg-available text-white shadow'
          : 'bg-white/10 hover:bg-white/20 text-white/90',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
